import { useEffect, useState, useCallback } from 'react';
import { Loader2, Plus, Edit2, Trash2, X } from 'lucide-react';
import {
  fieldPartnersApi,
  type IncentiveRule,
  type IncentiveTier,
  type FieldPartnerTier,
} from '@/api/field-partners';
import { formatINR } from '@/utils/formatters';
import { FieldProgramGate } from '@/components/field-partners/FieldProgramGate';
import type { FieldProgramConfig } from '@/hooks/useFieldProgramConfig';

function IncentiveRulesInner({ config }: { config: FieldProgramConfig | null }) {
  const partnerLabel = config?.partnerTypeLabel || 'Field Partner';

  const [rules, setRules] = useState<IncentiveRule[]>([]);
  const [tiers, setTiers] = useState<IncentiveTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<IncentiveRule> | null>(null);
  const [editingTier, setEditingTier] = useState<Partial<IncentiveTier> | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [r, t] = await Promise.all([
        fieldPartnersApi.listIncentiveRules().catch(() => []),
        fieldPartnersApi.listIncentiveTiers().catch(() => []),
      ]);
      setRules(r);
      setTiers(t);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load incentive config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const saveRule = async () => {
    if (!editing) return;
    try {
      if (editing.id) {
        await fieldPartnersApi.updateIncentiveRule(editing.id, editing);
      } else {
        await fieldPartnersApi.createIncentiveRule(editing);
      }
      setEditing(null);
      await fetch();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save');
    }
  };

  const deleteRule = async (id: string) => {
    if (!confirm('Delete this rule?')) return;
    try {
      await fieldPartnersApi.deleteIncentiveRule(id);
      await fetch();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  const saveTier = async () => {
    if (!editingTier) return;
    try {
      if (editingTier.id) {
        await fieldPartnersApi.updateIncentiveTier(editingTier.id, editingTier);
      } else {
        await fieldPartnersApi.createIncentiveTier(editingTier);
      }
      setEditingTier(null);
      await fetch();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save tier');
    }
  };

  const deleteTier = async (id: string) => {
    if (!confirm('Delete this tier?')) return;
    try {
      await fieldPartnersApi.deleteIncentiveTier(id);
      await fetch();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete tier');
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Incentive Rules</h1>
          <p className="text-sm text-[var(--text-muted)]">
            Per-unit payouts, upsell bonuses, tier thresholds, and special rewards for {partnerLabel.toLowerCase()}s
          </p>
        </div>
        <button
          onClick={() => setEditing({ name: '', perUnitPayout: 0, active: true })}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
        >
          <Plus size={16} />
          Add Rule
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      ) : (
        <>
          <div className="mb-6 overflow-hidden rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
            <div className="border-b border-[var(--border-primary)] bg-[var(--bg-inset)] px-4 py-2 text-xs font-semibold uppercase text-[var(--text-muted)]">
              Payout Rules
            </div>
            {rules.length === 0 ? (
              <p className="py-10 text-center text-sm text-[var(--text-muted)]">
                No rules configured
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--border-primary)] text-left text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
                    <th className="px-4 py-2">Name</th>
                    <th className="px-4 py-2">Category</th>
                    <th className="px-4 py-2 text-right">Per-unit</th>
                    <th className="px-4 py-2 text-right">Upsell Bonus</th>
                    <th className="px-4 py-2">Active</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-primary)]">
                  {rules.map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-3 font-medium text-[var(--text-primary)]">{r.name}</td>
                      <td className="px-4 py-3 text-[var(--text-secondary)]">
                        {r.productCategory || 'All'}
                      </td>
                      <td className="px-4 py-3 text-right text-[var(--text-primary)]">
                        {formatINR(r.perUnitPayout)}
                      </td>
                      <td className="px-4 py-3 text-right text-[var(--text-secondary)]">
                        {r.upsellBonus ? formatINR(r.upsellBonus) : '-'}
                        {r.upsellTrigger && (
                          <span className="ml-1 text-[10px] text-[var(--text-muted)]">
                            ({r.upsellTrigger})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${r.active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                        >
                          {r.active ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setEditing(r)}
                          className="mr-1 rounded p-1.5 text-blue-600 hover:bg-blue-50"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => deleteRule(r.id)}
                          className="rounded p-1.5 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="overflow-hidden rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
            <div className="flex items-center justify-between border-b border-[var(--border-primary)] bg-[var(--bg-inset)] px-4 py-2 text-xs font-semibold uppercase text-[var(--text-muted)]">
              <span>Tier Thresholds</span>
              <button
                onClick={() =>
                  setEditingTier({ tier: 'BRONZE', minInstallations: 0, multiplier: 1 })
                }
                className="flex items-center gap-1 rounded-md border border-[var(--border-primary)] px-2 py-0.5 text-[11px] font-medium normal-case text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]"
              >
                <Plus size={11} /> Add Tier
              </button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-primary)] text-left text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
                  <th className="px-4 py-2">Tier</th>
                  <th className="px-4 py-2 text-right">Min Installs</th>
                  <th className="px-4 py-2 text-right">Multiplier</th>
                  <th className="px-4 py-2">Quarterly Gift</th>
                  <th className="px-4 py-2">Special Reward</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-primary)]">
                {tiers.map((t) => (
                  <tr key={t.id}>
                    <td className="px-4 py-3 font-semibold">{t.tier}</td>
                    <td className="px-4 py-3 text-right text-[var(--text-primary)]">
                      {t.minInstallations}
                    </td>
                    <td className="px-4 py-3 text-right text-[var(--text-primary)]">
                      {t.multiplier}×
                    </td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">
                      {t.quarterlyGift || '-'}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">
                      {t.specialReward || '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setEditingTier(t)}
                        className="mr-1 rounded p-1.5 text-blue-600 hover:bg-blue-50"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => deleteTier(t.id)}
                        className="rounded p-1.5 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editing && (
        <RuleEditor
          value={editing}
          onClose={() => setEditing(null)}
          onChange={(v) => setEditing(v)}
          onSave={saveRule}
        />
      )}

      {editingTier && (
        <TierEditor
          value={editingTier}
          onClose={() => setEditingTier(null)}
          onChange={(v) => setEditingTier(v)}
          onSave={saveTier}
        />
      )}
    </div>
  );
}

function RuleEditor({
  value,
  onClose,
  onChange,
  onSave,
}: {
  value: Partial<IncentiveRule>;
  onClose: () => void;
  onChange: (v: Partial<IncentiveRule>) => void;
  onSave: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] shadow-xl">
        <div className="flex items-center justify-between border-b border-[var(--border-primary)] px-4 py-3">
          <h2 className="text-sm font-semibold">{value.id ? 'Edit Rule' : 'New Rule'}</h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-[var(--bg-surface-hover)]">
            <X size={16} />
          </button>
        </div>
        <div className="space-y-3 p-4 text-sm">
          <Field label="Name">
            <input
              value={value.name || ''}
              onChange={(e) => onChange({ ...value, name: e.target.value })}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2"
            />
          </Field>
          <Field label="Product Category (optional)">
            <input
              value={value.productCategory || ''}
              onChange={(e) => onChange({ ...value, productCategory: e.target.value })}
              placeholder="e.g. ss_tanks"
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2"
            />
          </Field>
          <Field label="Per-unit payout (₹)">
            <input
              type="number"
              value={value.perUnitPayout ?? 0}
              onChange={(e) => onChange({ ...value, perUnitPayout: Number(e.target.value) })}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2"
            />
          </Field>
          <Field label="Upsell bonus (₹, optional)">
            <input
              type="number"
              value={value.upsellBonus ?? 0}
              onChange={(e) => onChange({ ...value, upsellBonus: Number(e.target.value) })}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2"
            />
          </Field>
          <Field label="Upsell trigger (e.g. water_purifier)">
            <input
              value={value.upsellTrigger || ''}
              onChange={(e) => onChange({ ...value, upsellTrigger: e.target.value })}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2"
            />
          </Field>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!value.active}
              onChange={(e) => onChange({ ...value, active: e.target.checked })}
            />
            <span>Active</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-[var(--border-primary)] px-4 py-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-[var(--border-primary)] px-3 py-1.5 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-sm font-medium text-white"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function TierEditor({
  value,
  onClose,
  onChange,
  onSave,
}: {
  value: Partial<IncentiveTier>;
  onClose: () => void;
  onChange: (v: Partial<IncentiveTier>) => void;
  onSave: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] shadow-xl">
        <div className="flex items-center justify-between border-b border-[var(--border-primary)] px-4 py-3">
          <h2 className="text-sm font-semibold">
            {value.id ? `Edit Tier — ${value.tier}` : 'New Tier'}
          </h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-[var(--bg-surface-hover)]">
            <X size={16} />
          </button>
        </div>
        <div className="space-y-3 p-4 text-sm">
          <Field label="Tier">
            <select
              value={value.tier || 'BRONZE'}
              onChange={(e) => onChange({ ...value, tier: e.target.value as FieldPartnerTier })}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2"
            >
              <option value="BRONZE">BRONZE</option>
              <option value="SILVER">SILVER</option>
              <option value="GOLD">GOLD</option>
            </select>
          </Field>
          <Field label="Min installations">
            <input
              type="number"
              value={value.minInstallations ?? 0}
              onChange={(e) => onChange({ ...value, minInstallations: Number(e.target.value) })}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2"
            />
          </Field>
          <Field label="Multiplier">
            <input
              type="number"
              step="0.1"
              value={value.multiplier ?? 1}
              onChange={(e) => onChange({ ...value, multiplier: Number(e.target.value) })}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2"
            />
          </Field>
          <Field label="Quarterly gift">
            <input
              value={value.quarterlyGift || ''}
              onChange={(e) => onChange({ ...value, quarterlyGift: e.target.value })}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2"
            />
          </Field>
          <Field label="Special reward (e.g. Thailand trip)">
            <input
              value={value.specialReward || ''}
              onChange={(e) => onChange({ ...value, specialReward: e.target.value })}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2"
            />
          </Field>
        </div>
        <div className="flex justify-end gap-2 border-t border-[var(--border-primary)] px-4 py-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-[var(--border-primary)] px-3 py-1.5 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-sm font-medium text-white"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">{label}</label>
      {children}
    </div>
  );
}

export function IncentiveRulesPage() {
  return <FieldProgramGate>{(config) => <IncentiveRulesInner config={config} />}</FieldProgramGate>;
}

export default IncentiveRulesPage;
